import { useState, useEffect } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Gift, Users, Copy, Check, DollarSign, TrendingUp,
  Share2, Mail, Twitter, Linkedin
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

export default function ReferralDashboardPage() {
  const [referralData, setReferralData] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [applyCode, setApplyCode] = useState("");
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      const [codeRes, referralsRes, configRes] = await Promise.all([
        axios.get(`${API}/referral/my-code`, { withCredentials: true }),
        axios.get(`${API}/referral/my-referrals`, { withCredentials: true }),
        axios.get(`${API}/referral/config`, { withCredentials: true })
      ]);
      
      setReferralData(codeRes.data);
      setReferrals(referralsRes.data.referrals);
      setStats(referralsRes.data.stats);
      setConfig(configRes.data);
    } catch (error) {
      console.error('Error fetching referral data:', error);
      toast.error("Failed to load referral data");
    } finally {
      setLoading(false);
    }
  };

  const copyReferralCode = () => {
    if (referralData?.referral_code) {
      navigator.clipboard.writeText(referralData.referral_code);
      setCopied(true);
      toast.success("Referral code copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const copyReferralLink = () => {
    if (referralData?.referral_code) {
      const link = `${window.location.origin}/signup?ref=${referralData.referral_code}`;
      navigator.clipboard.writeText(link);
      toast.success("Referral link copied!");
    }
  };

  const handleApplyCode = async () => {
    if (!applyCode.trim()) {
      toast.error("Please enter a referral code");
      return;
    }
    
    setApplying(true);
    try {
      const response = await axios.post(`${API}/referral/apply`, { code: applyCode }, { withCredentials: true });
      toast.success(response.data.message);
      setApplyCode("");
      fetchReferralData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply referral code");
    } finally {
      setApplying(false);
    }
  };

  const shareOnTwitter = () => {
    const text = `I'm using FlowSync for workflow automation and it's amazing! Sign up with my referral code ${referralData?.referral_code} and get ${config?.referred_reward_value}% off!`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank');
  };

  const shareOnLinkedIn = () => {
    const link = `${window.location.origin}/signup?ref=${referralData?.referral_code}`;
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}`, '_blank');
  };

  const shareViaEmail = () => {
    const subject = "Check out FlowSync - Workflow Automation";
    const body = `Hey!\n\nI've been using FlowSync for workflow automation and thought you might like it too.\n\nUse my referral code ${referralData?.referral_code} to get ${config?.referred_reward_value}% off your first purchase!\n\nSign up here: ${window.location.origin}/signup?ref=${referralData?.referral_code}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!config?.is_enabled) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-96 text-center p-6">
          <Gift className="w-16 h-16 text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold mb-2">Referral Program Unavailable</h2>
          <p className="text-muted-foreground">The referral program is currently not active. Check back later!</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="referral-dashboard-page">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center mx-auto mb-4">
            <Gift className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-heading font-bold mb-2">Referral Program</h1>
          <p className="text-muted-foreground">
            Share FlowSync with friends and earn rewards! You get{" "}
            <span className="text-primary font-semibold">{config?.referrer_reward_value}% off</span> and they get{" "}
            <span className="text-primary font-semibold">{config?.referred_reward_value}% off</span>.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
          <Card>
            <CardContent className="p-4 text-center">
              <Users className="w-8 h-8 text-blue-500 mx-auto mb-2" />
              <p className="text-2xl font-bold">{stats?.total_referrals || 0}</p>
              <p className="text-sm text-muted-foreground">Total Referrals</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Check className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <p className="text-2xl font-bold">{stats?.successful_referrals || 0}</p>
              <p className="text-sm text-muted-foreground">Successful</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <DollarSign className="w-8 h-8 text-amber-500 mx-auto mb-2" />
              <p className="text-2xl font-bold">${stats?.total_rewards?.toFixed(2) || '0.00'}</p>
              <p className="text-sm text-muted-foreground">Total Rewards</p>
            </CardContent>
          </Card>
        </div>

        {/* Referral Code Card */}
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle>Your Referral Code</CardTitle>
            <CardDescription>Share this code with friends to earn rewards</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="flex-1 p-4 bg-muted rounded-lg text-center">
                <code className="text-2xl font-bold tracking-wider">{referralData?.referral_code}</code>
              </div>
              <Button onClick={copyReferralCode} variant="outline" size="lg">
                {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
              </Button>
            </div>

            <div className="flex flex-wrap gap-2 justify-center">
              <Button variant="outline" onClick={copyReferralLink}>
                <Share2 className="w-4 h-4 mr-2" /> Copy Link
              </Button>
              <Button variant="outline" onClick={shareViaEmail}>
                <Mail className="w-4 h-4 mr-2" /> Email
              </Button>
              <Button variant="outline" onClick={shareOnTwitter}>
                <Twitter className="w-4 h-4 mr-2" /> Twitter
              </Button>
              <Button variant="outline" onClick={shareOnLinkedIn}>
                <Linkedin className="w-4 h-4 mr-2" /> LinkedIn
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Apply Referral Code */}
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle>Have a Referral Code?</CardTitle>
            <CardDescription>Enter a friend's referral code to get your discount</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input
                value={applyCode}
                onChange={(e) => setApplyCode(e.target.value.toUpperCase())}
                placeholder="Enter referral code"
                className="font-mono"
              />
              <Button onClick={handleApplyCode} disabled={applying}>
                {applying ? "Applying..." : "Apply"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Referral History */}
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle>Your Referrals</CardTitle>
            <CardDescription>Track who you've referred</CardDescription>
          </CardHeader>
          <CardContent>
            {referrals.length > 0 ? (
              <ScrollArea className="h-64">
                <div className="space-y-3">
                  {referrals.map((ref) => (
                    <div key={ref.referral_id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div>
                        <p className="font-medium">{ref.referred_name}</p>
                        <p className="text-sm text-muted-foreground">{ref.referred_email}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(ref.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Badge variant={ref.status === "completed" ? "default" : ref.status === "rewarded" ? "success" : "secondary"}>
                        {ref.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No referrals yet</p>
                <p className="text-sm">Share your code to start earning rewards!</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* How it Works */}
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle>How It Works</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold text-primary">1</span>
                </div>
                <h4 className="font-medium mb-1">Share Your Code</h4>
                <p className="text-sm text-muted-foreground">Send your unique referral code to friends</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold text-primary">2</span>
                </div>
                <h4 className="font-medium mb-1">They Sign Up</h4>
                <p className="text-sm text-muted-foreground">Your friend creates an account and subscribes</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold text-primary">3</span>
                </div>
                <h4 className="font-medium mb-1">Both Get Rewarded</h4>
                <p className="text-sm text-muted-foreground">You both receive discounts on your next bill</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
